import { VALIDATION_RULES } from '@localstack/services';
import {
  ControlledTextField,
  ControlledCheckbox,
  ProgressButton,
  NewsletterSubscriptionLine,
  CompleteProfileLegalFineprint,
  EnterLocalStackLegalFineprint,
  StartPreviewLegalFineprint,
  StartTrialLegalFineprint,
} from '@localstack/ui';
import { Box, styled, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { THIRTY_DAY_TRIAL_DURATION } from '@localstack/constants';

import { useStyles } from '../../common';
import { FinishAccountCreationFormData, PlanChoice } from '../types';
import { canChoosePlanBasedOnEmulatorChoice, getTermsForEmulator } from '../utils';
import { emulatorOptions } from '../emulators';

import { EmulatorChoiceOption, EmulatorChoiceSection } from './EmulatorChoice';
import { PlanChoiceSection } from './PlanChoice';

/** Vertical stack of form fields, sharing one filled background. */
const FieldsColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(1),
  '& .MuiOutlinedInput-root': {
    background: theme.palette.background.default,
  },
}));

/** The legal fineprint renders left-aligned by default, but the card is centered. */
const CenteredFineprint = styled(Box)({
  '&& .MuiTypography-root': {
    textAlign: 'center',
  },
});

type Props = {
  control: Control<FinishAccountCreationFormData, any>; // eslint-disable-line
  planChoice: PlanChoice;
  onPlanChoiceChange: (choice: PlanChoice) => void;
  isSigningUp: boolean;
  showPlanSelection?: boolean;
  showStudentPlanFlow?: boolean;
};

export const PersonalDetailsStep = ({
  control,
  isSigningUp,
  planChoice,
  onPlanChoiceChange,
  showPlanSelection,
  showStudentPlanFlow,
}: Props): ReactElement => {
  const classes = useStyles();

  const selectedEmulator = useWatch({ control, name: 'emulator' });

  const showPlanChoice = showPlanSelection && canChoosePlanBasedOnEmulatorChoice(selectedEmulator);
  const isTrial = planChoice === PlanChoice.TRIAL;
  const isPreview = planChoice === PlanChoice.PREVIEW;
  const showCompanyField = !showStudentPlanFlow && planChoice !== PlanChoice.FREE;

  const emulatorChoices: EmulatorChoiceOption[] = emulatorOptions.map(({ value, headingColor, icon }) => ({
    value,
    headingColor,
    icon,
    terms: getTermsForEmulator(value, planChoice),
  }));

  const getSubmitLabel = (): string => {
    if (!showPlanSelection) return 'Continue';
    if (isPreview) return 'Enable Preview';
    return isTrial ? 'Start Trial' : 'Enter LocalStack';
  };

  const getLegalFineprint = (): ReactElement => {
    if (!showPlanSelection) return <CompleteProfileLegalFineprint />;
    if (planChoice === PlanChoice.FREE) return <EnterLocalStackLegalFineprint />;
    return isPreview ? <StartPreviewLegalFineprint /> : <StartTrialLegalFineprint />;
  };

  return (
    <>
      {showPlanSelection && (
        <Box mt={2}>
          <EmulatorChoiceSection control={control} options={emulatorChoices} />
          {showPlanChoice && <PlanChoiceSection planChoice={planChoice} onPlanChoiceChange={onPlanChoiceChange} />}
        </Box>
      )}

      <FieldsColumn mt={2}>
        <Box display="flex" gap={1}>
          <ControlledTextField
            size="small"
            control={control}
            showErrorsOnlyIfTouched={false}
            name="firstname"
            fullWidth
            placeholder="First Name"
            type="text"
            variant="outlined"
            rules={VALIDATION_RULES.safeStringRequired}
          />
          <ControlledTextField
            size="small"
            control={control}
            showErrorsOnlyIfTouched={false}
            name="lastname"
            fullWidth
            placeholder="Last Name"
            type="text"
            variant="outlined"
            rules={VALIDATION_RULES.safeStringRequired}
          />
        </Box>

        {showCompanyField && (
          <ControlledTextField
            size="small"
            control={control}
            name="company"
            fullWidth
            placeholder="Company"
            type="text"
            variant="outlined"
            rules={VALIDATION_RULES.safeStringOptional}
          />
        )}

        <ControlledTextField
          size="small"
          control={control}
          showErrorsOnlyIfTouched={false}
          name="job_title"
          fullWidth
          placeholder="Job Title"
          type="text"
          variant="outlined"
          rules={VALIDATION_RULES.safeStringRequired}
        />

        <Box textAlign="left">
          <ControlledCheckbox
            control={control}
            name="newsletter"
            size="small"
            formControlLabelProps={{
              classes: {
                root: classes.formControlLabelRoot,
                asterisk: classes.hiddenAsterik,
              },
            }}
            label={<NewsletterSubscriptionLine />}
          />
        </Box>

        <ProgressButton variant="contained" loading={isSigningUp} fullWidth size="xlarge" type="submit" color="primary">
          {getSubmitLabel()}
        </ProgressButton>

        {showPlanSelection && isTrial && (
          <Typography fontSize={13} color="text.tertiary" fontWeight={500}>
            Clicking the button above will begin your {THIRTY_DAY_TRIAL_DURATION}-day trial.
          </Typography>
        )}

        <CenteredFineprint>{getLegalFineprint()}</CenteredFineprint>
      </FieldsColumn>
    </>
  );
};
